import { Copy, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BadgeCourier from '@/domains/dashboard-management/components/BadgeCourier/BadgeCourier';
import DetailInfo from '@/domains/dashboard-management/components/DetailInfo/DetailInfo';
import WithdrawalHistory from '@/domains/dashboard-management/components/WithdrawalHistory/WithdrawalHistory';
import { useAdminAffiliate } from '@/domains/dashboard-management/hooks/useAdminAffiliates';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import BackAction from '@/shared/components/ui/BackAction/BackAction';
import Breadcrumb from '@/shared/components/ui/Breadcrumb/Breadcrumb';
import Button from '@/shared/components/ui/Button';

const QR_SIZE = 200;

const rupiah = (value: string | number) => `Rp ${formatPrice(Number(value))}`;

const AffiliateDetailManagement = () => {
  const [searchParams] = useSearchParams();
  const affiliateId = searchParams.get('id') ?? undefined;
  const { data: member, isLoading, isError } = useAdminAffiliate(affiliateId);

  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  const qrRef = useRef<HTMLCanvasElement | null>(null);
  const link = member?.referral_link ?? '';

  const handleCopyLink = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }, [link]);

  // Saves the QR exactly as drawn, so it always encodes the current link
  const handleDownloadQr = useCallback(() => {
    const canvas = qrRef.current;
    if (!canvas || !member) return;
    const anchor = document.createElement('a');
    anchor.href = canvas.toDataURL('image/png');
    anchor.download = `qr-affiliate-${member.referral_code}.png`;
    anchor.click();
  }, [member]);

  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, []);

  const header = (
    <div className="inline-flex gap-3 w-full items-center flex-wrap">
      <BackAction />
      <Breadcrumb items={[{ label: 'Affiliate' }, { label: member?.name ?? 'Detail Anggota' }]} />
    </div>
  );

  if (!member) {
    return (
      <section className="flex! flex-col gap-6 overflow-auto mb-10">
        {header}
        <div className="shadowCard p-6">
          <p className="text-center">
            {!affiliateId
              ? 'Anggota tidak ditemukan'
              : isLoading
                ? 'Memuat...'
                : isError
                  ? 'Gagal memuat anggota'
                  : 'Anggota tidak ditemukan'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      {header}

      <div className="shadowCard flex flex-col">
        <div className="w-full p-3 lg:p-6 flex flex-wrap gap-3 justify-between">
          <div className="shrink-0 min-w-0 flex-1 ">
            <p className="title1bold capitalize">Detail Anggota</p>
            <p className="body1 text-text-grey break-all shrink-0">{member.affiliate_id}</p>
          </div>
          {member.status !== 'active' && (
            <BadgeCourier statusCourier="Unavailable" label={member.status_label} />
          )}
        </div>

        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />

        <section className="w-full p-3 lg:p-6 flex!  flex-wrap gap-6 justify-between">
          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="shrink-0">
              <p className="title2bold capitalize">Informasi Anggota</p>
              <p className="body2 text-text-grey">Detail anggota affiliate</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:grid-cols-4 md:grid-cols-2">
              <DetailInfo title="ID" value={member.affiliate_id} />
              <DetailInfo title="TOTAL KLIK LINK" value={member.click_total} />
              <DetailInfo title="TOTAL PEMBELIAN" value={rupiah(member.purchase_total)} />
              <DetailInfo title="SALDO SAAT INI" value={rupiah(member.balance)} />
              <DetailInfo title="NAMA ANGGOTA" value={member.name ?? '-'} />
              <DetailInfo title="TOTAL TRANSAKSI" value={member.transaction_total} />
              <DetailInfo title="TOTAL KOMISI" value={rupiah(member.commission_total)} />
            </div>
          </article>

          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="shrink-0">
              <p className="title2bold capitalize">Link & QR Code</p>
              <p className="body2 text-text-grey">
                Gunakan QR Code atau tautan di bawah untuk membagikan halaman produk Anda.
              </p>
              {!member.link_active && (
                <p className="body2 text-[#FF3B30] mt-1">
                  Akun {member.status_label?.toLowerCase() ?? 'tidak aktif'}: tautan ini tidak
                  mencatat klik maupun pembelian.
                </p>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-fit flex-col flex gap-3 shrink-0">
                <p className="title2medium text-text-grey uppercase">QR Code</p>
                <div className="flex justify-center items-center w-fit flex-col gap-3">
                  <QRCodeCanvas
                    ref={qrRef}
                    value={link}
                    size={QR_SIZE}
                    marginSize={2}
                    title={link}
                    aria-label="QR code affiliate"
                  />
                  <Button customButton="btn-fill-white w-fit" onClick={handleDownloadQr}>
                    <Download />
                    <span className="body1bold">Unduh QR</span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-3 flex-1 min-w-0">
                <p className="title2medium text-text-grey uppercase">Link</p>
                <div className="flex items-center gap-3 w-full max-w-[520px] rounded-xl border border-blue-cs-200 bg-[#F6F6F6] px-4 py-3">
                  <span
                    className="body1 text-black dark:text-white truncate min-w-0 flex-1"
                    title={link}
                  >
                    {link}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    aria-label="Salin link"
                    className="shrink-0 inline-flex items-center gap-1 text-text-blue-100 transition hover:opacity-80"
                  >
                    {copied && <span className="body2">Tersalin</span>}
                    <Copy size={18} strokeWidth={1.8} />
                  </button>
                </div>
              </div>
            </div>
          </article>

          <WithdrawalHistory
            affiliateId={member.affiliate_id}
            description="Lihat dan kelola riwayat keanggotaan affiliate."
          />
        </section>
      </div>
    </section>
  );
};

export default AffiliateDetailManagement;
